import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '../../utils.js';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    components,
    ...props
}: CalendarProps) {
    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('p-3', className)}
            classNames={{
                months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                month: 'space-y-4',
                caption_label: 'text-sm font-medium',
                month_grid: 'w-full border-collapse space-y-1',
                weekdays: 'flex',
                weekday: 'text-foreground-subtle rounded-md w-9 font-normal text-[0.8rem] opacity-60',
                week: 'flex w-full mt-2',
                day: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20',
                day_button: cn('h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-foreground/[0.05] rounded-xl transition-all'),
                range_start: 'bg-primary text-primary-foreground rounded-xl',
                range_end: 'bg-primary text-primary-foreground rounded-xl',
                selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-xl',
                today: 'bg-foreground/[0.08] text-foreground font-bold rounded-xl',
                outside: 'day-outside text-muted-foreground opacity-30 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
                disabled: 'text-muted-foreground opacity-50',
                range_middle: 'aria-selected:bg-primary/15 aria-selected:text-foreground !rounded-none',
                hidden: 'invisible',
                month_caption: 'flex justify-center h-10 relative items-center gap-1 px-10 mb-6',
                nav: 'absolute inset-x-0 flex items-center justify-between px-2 z-20 pointer-events-none',
                button_previous: 'pointer-events-auto h-8 w-8 relative top-[20px] md:top-[5px] flex items-center justify-center !text-white hover:bg-white/10 transition-all rounded-lg',
                button_next: 'pointer-events-auto h-8 w-8 relative top-[20px] md:top-[5px] flex items-center justify-center !text-white hover:bg-white/10 transition-all rounded-lg',
                ...classNames,
            }}
            components={{
                Chevron: ({ orientation }) => {
                    const Icon = orientation === 'left' ? ChevronLeft : ChevronRight;
                    return <Icon className="h-4 w-4 text-foreground stroke-[2.5px]" />;
                },
                ...components,
            }}
            {...props}
        />
    );
}
Calendar.displayName = 'Calendar';

export { Calendar };
